import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  createManualBankTransferTestInvoice,
  createSystemStripeCheckoutSession,
  downloadBillingInvoicePdf,
  generateBillingInvoicePdf,
  getCustomerBillingOverview,
  getCustomerBillingProfile,
  getCustomerInvoices,
  previewCustomerBillingTaxRule,
  updateCustomerBillingProfile,
  verifyCustomerBillingVatId,
} from '../api/billingPortalApi';
import { fetchSubscriptions } from '../api/authApi';
import { createStripeTestCheckoutInvoice, getStripeStatus } from '../api/stripeApi';
import {
  commercialAgreementsControls,
  commercialAgreementsEnabledFromOverview,
} from '../billing/commercialAgreementsUi';
import { stripeOnlinePaymentControlsFromStripeStatus } from '../billing/stripeOnlinePaymentUi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { InfoCard } from './InfoCard';
import { InvoiceIssuanceModesPanel } from './InvoiceIssuanceModesPanel';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { Button } from './ui/Button';
import { Select, SelectOption } from './ui/Select';
import { TextInput } from './ui/TextInput';
import { CustomerPriceAgreementsPanel } from './CustomerPriceAgreementsPanel';

const { semantic } = colorTokens;

function resolveMessage(payload, t, fallback) {
  const rawKey = payload?.message_key;
  if (rawKey) {
    const i18nKey = String(rawKey).startsWith('stripe.')
      ? `stripe:${String(rawKey).slice('stripe.'.length)}`
      : String(rawKey).startsWith('billing.')
        ? `billing:${String(rawKey).slice('billing.'.length)}`
        : rawKey;
    return t(i18nKey, { defaultValue: payload.message || fallback });
  }
  return payload?.message || fallback;
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

export function CustomerBillingTab({ customerId }) {
  const { t } = useTranslation(['billing', 'stripe', 'common', 'customers']);
  const { session } = useAuth();
  const canSync = hasPermission(session, 'system.stripe.sync');
  const canManageInvoices = hasPermission(session, 'system.billing.invoices.manage');
  const canManageAgreements = hasPermission(session, 'system.billing.price_agreements.manage')
    || hasPermission(session, 'system.billing.price_agreements.view');
  const [agreementsUnavailable, setAgreementsUnavailable] = useState(false);
  const [qaInvoice, setQaInvoice] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);

  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({});
  const [taxPreview, setTaxPreview] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [overviewError, setOverviewError] = useState('');
  const [profileError, setProfileError] = useState('');
  const [invoicesError, setInvoicesError] = useState('');

  const load = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    setOverviewError('');
    setProfileError('');
    setInvoicesError('');

    // Stripe status must not gate OMEGA / invoice UI — a hung /api/system/stripe/status
    // previously left this tab on LoadingSkeleton forever (R2/R6 P1).
    getStripeStatus()
      .then((value) => setStripeStatus(value))
      .catch(() => setStripeStatus(null));

    // Fail-open watchdog: any hung billing API must not block external-accounting UI.
    const watchdog = setTimeout(() => setLoading(false), 12_000);

    Promise.allSettled([
      getCustomerBillingOverview(customerId),
      getCustomerBillingProfile(customerId),
      getCustomerInvoices(customerId),
      fetchSubscriptions({ customer_id: customerId }),
    ]).then(([overviewResult, profileResult, invoicesResult, subscriptionsResult]) => {
      if (overviewResult.status === 'fulfilled') {
        const nextOverview = overviewResult.value;
        setOverview(nextOverview);
        // Re-sync with server capability; clear transient FE-before-BE unavailable latch.
        if (commercialAgreementsEnabledFromOverview(nextOverview)) {
          setAgreementsUnavailable(false);
        }
      } else {
        setOverviewError(t('billing:overview_load_failed'));
      }

      if (profileResult.status === 'fulfilled') {
        const profile = profileResult.value || {};
        setForm(profile);
        setTaxPreview(profile.tax_rule ? {
          tax_rule: profile.tax_rule,
          tax_rate: profile.tax_rate,
          tax_note: profile.tax_note,
          tax_label: profile.tax_rule,
          requires_manual_review: profile.requires_manual_tax_review,
        } : null);
      } else {
        setProfileError(t('billing:profile_load_failed'));
      }

      if (invoicesResult.status === 'fulfilled') {
        setInvoices(invoicesResult.value.data || []);
      } else {
        setInvoices([]);
        setInvoicesError(t('billing:invoices_load_failed'));
      }

      if (subscriptionsResult.status === 'fulfilled') {
        const rows = subscriptionsResult.value?.data || subscriptionsResult.value || [];
        setSubscriptions(Array.isArray(rows) ? rows : []);
      }
    }).finally(() => {
      clearTimeout(watchdog);
      setLoading(false);
    });
  }, [customerId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      setBusy(true);
      const result = await updateCustomerBillingProfile(customerId, form);
      setForm(result.profile || {});
      setTaxPreview(result.tax_preview || null);
      setMessage(t('customers:billingProfile.saved'));
    } catch {
      setMessage(t('billing:profile_update_failed'));
    } finally {
      setBusy(false);
    }
  };

  const previewTax = async () => {
    try {
      setBusy(true);
      const preview = await previewCustomerBillingTaxRule(customerId, {
        country: form.country || form.country_code,
        customer_type: form.customer_type,
        vat_id: form.vat_id,
        vat_validation_status: form.vat_validation_status,
      });
      setTaxPreview(preview);
    } catch {
      setError(t('billing:profile_update_failed'));
    } finally {
      setBusy(false);
    }
  };

  const verifyVat = async () => {
    try {
      setBusy(true);
      const result = await verifyCustomerBillingVatId(customerId);
      if (result?.data?.profile) {
        setForm(result.data.profile);
      }
      if (result?.data?.tax_preview) {
        setTaxPreview(result.data.tax_preview);
      }
      setMessage(result?.message || t('customers:billingProfile.viesDisabled'));
    } catch {
      setError(t('customers:billingProfile.viesDisabled'));
    } finally {
      setBusy(false);
    }
  };

  const taxRuleLabel = (rule) => {
    const map = {
      domestic_vat: 'billing:taxRules.domesticVat',
      eu_b2b_reverse_charge: 'billing:taxRules.euB2bReverseCharge',
      eu_b2c_country_vat: 'billing:taxRules.euB2cCountryVat',
      non_eu_manual_review: 'billing:taxRules.nonEuManualReview',
      manual_review: 'billing:taxRules.manualReview',
      non_vat_payer: 'billing:taxRules.nonVatPayer',
    };
    return rule ? t(map[rule] || rule, { defaultValue: rule }) : '—';
  };

  const createTestInvoice = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await createStripeTestCheckoutInvoice(customerId);
      const data = res.data || res;
      setMessage(resolveMessage(data, t, t('stripe:messages.testCheckoutInvoiceCreated')));
      await load();
    } catch (err) {
      setError(resolveMessage(err?.response?.data || {}, t, t('billing:stripe_checkout_failed')));
    } finally {
      setBusy(false);
    }
  };

  const createManualBankTransferQaInvoice = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    setQaInvoice(null);
    try {
      const data = await createManualBankTransferTestInvoice({
        customer_id: Number(customerId),
        confirmation: 'create-manual-bank-transfer-test-invoice',
        generate_pdf: true,
      });
      setQaInvoice(data.invoice || data);
      const qrStatus = data.invoice?.payment_qr_status || data.qr?.status;
      if (qrStatus === 'generated') {
        setMessage(t('billing:qa.manualBankTransferCreated'));
      } else {
        setError(
          data.qr?.safe_error_message
            || t('billing:qa.qrNotGenerated', { status: qrStatus || 'unavailable' }),
        );
      }
      await load();
    } catch (err) {
      setError(resolveMessage(err?.response?.data || {}, t, t('billing:qa.createFailed')));
    } finally {
      setBusy(false);
    }
  };

  const agreementsUi = commercialAgreementsControls({
    hasPermission: canManageAgreements,
    overviewEnabled: commercialAgreementsEnabledFromOverview(overview),
    unavailable: agreementsUnavailable,
  });

  const openCheckout = async (invoiceId) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const session = await createSystemStripeCheckoutSession(invoiceId);
      if (session.status === 'disabled') {
        setError(t('stripe:errors.checkoutDisabled'));
        return;
      }
      const url = session.checkout_url || session.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        setMessage(t('stripe:messages.checkoutSessionCreated'));
        return;
      }
      setError(resolveMessage(session, t, t('billing:stripe_checkout_failed')));
    } catch (err) {
      setError(resolveMessage(err?.response?.data || {}, t, t('billing:stripe_checkout_failed')));
    } finally {
      setBusy(false);
    }
  };

  const handleGeneratePdf = async (invoiceId) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await generateBillingInvoicePdf(invoiceId);
      if (result.status === 'generated') {
        setMessage(t('billing:invoices.pdf.generated'));
        const invoicesResult = await getCustomerInvoices(customerId);
        setInvoices(invoicesResult.data || []);
        return;
      }
      setError(result.details?.error_message || t('billing:invoices.pdf.failed'));
      const invoicesResult = await getCustomerInvoices(customerId);
      setInvoices(invoicesResult.data || []);
    } catch (err) {
      setError(err?.response?.data?.details?.error_message || t('billing:invoices.pdf.failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    setBusy(true);
    setError('');
    try {
      const blob = await downloadBillingInvoicePdf(invoice.id);
      triggerBlobDownload(blob, `${invoice.public_number || `invoice-${invoice.id}`}.pdf`);
    } catch {
      setError(t('billing:download_failed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  const isTrial = overview?.payment_status === 'trial' || overview?.trial_ends_at;
  const openInvoices = invoices.filter((inv) => !['paid', 'void', 'canceled'].includes(inv.status));
  const showTestInvoice = canSync && stripeStatus?.allow_test_checkout_invoice && stripeStatus?.checkout_enabled;
  const stripeUi = stripeOnlinePaymentControlsFromStripeStatus(stripeStatus);
  const showManualBankQa = canManageInvoices && process.env.NODE_ENV === 'development';
  const sectionTitleSx = {
    fontFamily: typographyTokens.fontFamily,
    fontWeight: typographyTokens.fontWeightSemibold,
    fontSize: 14,
    lineHeight: '28px',
    color: semantic.text.primary,
    mb: 1,
  };

  return (
    <Box>
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {overviewError && <Alert severity="error" sx={{ mb: 2 }}>{overviewError}</Alert>}

      {overview && (
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard label={t('billing:open_invoices')} value={overview.open_invoices ?? 0} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard label={t('billing:amount_due')} value={overview.amount_due ?? 0} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard label={t('billing:plan')} value={overview.current_plan?.name || '—'} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button component={RouterLink} to="/billing/invoices" variant="Secondary" fullWidth>
              {t('billing:all_invoices')}
            </Button>
          </Grid>
        </Grid>
      )}

      {isTrial && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('billing:trial_mode_hint')}</Alert>
      )}

      {showTestInvoice && (
        <SectionCard variant="Default" title={t('stripe:actions.createTestCheckoutInvoice')}>
          <Typography sx={{ mb: 1.5, fontSize: 13, color: semantic.text.secondary }}>
            {t('stripe:labels.testModeOnly')}
          </Typography>
          <Button variant="Primary" disabled={busy} onClick={createTestInvoice}>
            {t('stripe:actions.createTestCheckoutInvoice')}
          </Button>
        </SectionCard>
      )}

      {agreementsUi.showPanel && (
        <CustomerPriceAgreementsPanel
          customerId={customerId}
          subscriptions={subscriptions}
          onUnavailable={() => setAgreementsUnavailable(true)}
        />
      )}

      <InvoiceIssuanceModesPanel
        customerId={customerId}
        subscriptions={subscriptions}
        canManageInvoices={canManageInvoices}
        agreementsUnavailable={agreementsUnavailable}
        setAgreementsUnavailable={setAgreementsUnavailable}
        overview={overview}
        busy={busy}
        setBusy={setBusy}
        setMessage={setMessage}
        setError={setError}
        onIssued={load}
      />

      {showManualBankQa && (
        <SectionCard variant="Default" title={t('billing:qa.createManualBankTransfer')}>
          <Typography sx={{ mb: 1.5, fontSize: 13, color: semantic.text.secondary }}>
            {t('billing:qa.createManualBankTransferHint')}
          </Typography>
          <Button variant="Secondary" disabled={busy} onClick={createManualBankTransferQaInvoice}>
            {t('billing:qa.createManualBankTransfer')}
          </Button>
          {qaInvoice && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('billing:invoice_number')}: {qaInvoice.public_number}
              </Typography>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('billing:payment_method')}: {qaInvoice.payment_method}
              </Typography>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('billing:invoices.qr.status')}: {qaInvoice.payment_qr_status}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  variant="Secondary"
                  component={RouterLink}
                  to={`/billing/invoices/${qaInvoice.id}`}
                >
                  {t('billing:qa.openInvoice')}
                </Button>
              </Stack>
            </Box>
          )}
        </SectionCard>
      )}

      <Typography sx={sectionTitleSx}>{t('customers:billingProfile.title')}</Typography>
      {profileError && <Alert severity="warning" sx={{ mb: 2 }}>{profileError}</Alert>}
      <SectionCard variant="Default">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextInput
              density="Compact"
              label={t('customers:billingProfile.billingName')}
              value={form.company_name || form.billing_name || ''}
              onChange={(e) => setForm({ ...form, company_name: e.target.value, billing_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              density="Compact"
              label={t('customers:billingProfile.billingEmail')}
              value={form.billing_email || ''}
              onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              density="Compact"
              label={t('customers:billingProfile.addressLine1')}
              value={form.address_line1 || ''}
              onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              density="Compact"
              label={t('customers:billingProfile.addressLine2')}
              value={form.address_line2 || ''}
              onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput
              density="Compact"
              label={t('customers:billingProfile.city')}
              value={form.city || ''}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput
              density="Compact"
              label={t('customers:billingProfile.postalCode')}
              value={form.postal_code || ''}
              onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput
              density="Compact"
              label={t('customers:billingProfile.countryCode')}
              value={form.country || ''}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
              helperText="SK, CZ, DE…"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Select
              density="Compact"
              fullWidth
              label={t('customers:billingProfile.customerType')}
              value={form.customer_type || 'unknown'}
              onChange={(e) => setForm({ ...form, customer_type: e.target.value })}
            >
              <SelectOption value="unknown">{t('customers:billingProfile.customerTypeUnknown')}</SelectOption>
              <SelectOption value="business">{t('customers:billingProfile.customerTypeBusiness')}</SelectOption>
              <SelectOption value="consumer">{t('customers:billingProfile.customerTypeConsumer')}</SelectOption>
            </Select>
          </Grid>
          {(form.customer_type || 'unknown') === 'business' && (
            <>
              <Grid item xs={12} md={4}>
                <TextInput
                  density="Compact"
                  label={t('customers:billingProfile.companyId')}
                  helperText={t('customers:billingProfile.companyIdRecommended')}
                  value={form.company_id_number || form.company_id || ''}
                  onChange={(e) => setForm({ ...form, company_id_number: e.target.value, company_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextInput
                  density="Compact"
                  label={t('customers:billingProfile.taxId')}
                  helperText={t('customers:billingProfile.taxIdRecommended')}
                  value={form.tax_id || ''}
                  onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  density="Compact"
                  label={t('customers:billingProfile.vatId')}
                  helperText={t('customers:billingProfile.vatIdRecommended')}
                  value={form.vat_id || ''}
                  onChange={(e) => setForm({ ...form, vat_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  density="Compact"
                  label={t('customers:billingProfile.vatValidationStatus')}
                  value={form.vat_validation_status || '—'}
                  readOnly
                />
              </Grid>
            </>
          )}
          {(form.customer_type || 'unknown') === 'consumer' && (
            <Grid item xs={12}>
              <Alert severity="info">{t('customers:billingProfile.consumerNoCompanyIds')}</Alert>
            </Grid>
          )}
          {(form.customer_type || 'unknown') === 'unknown' && (
            <>
              <Grid item xs={12} md={4}>
                <TextInput
                  density="Compact"
                  label={t('customers:billingProfile.companyId')}
                  value={form.company_id_number || form.company_id || ''}
                  onChange={(e) => setForm({ ...form, company_id_number: e.target.value, company_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextInput
                  density="Compact"
                  label={t('customers:billingProfile.taxId')}
                  value={form.tax_id || ''}
                  onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  density="Compact"
                  label={t('customers:billingProfile.vatId')}
                  value={form.vat_id || ''}
                  onChange={(e) => setForm({ ...form, vat_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  density="Compact"
                  label={t('customers:billingProfile.vatValidationStatus')}
                  value={form.vat_validation_status || '—'}
                  readOnly
                />
              </Grid>
            </>
          )}
        </Grid>

        {(taxPreview || form.tax_note) && (
          <Alert severity={taxPreview?.requires_manual_review || form.requires_manual_tax_review ? 'warning' : 'info'} sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('customers:billingProfile.taxRulePreview')}: {taxRuleLabel(taxPreview?.tax_rule || form.tax_rule)}
              {taxPreview?.tax_rate != null ? ` · ${taxPreview.tax_rate} %` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {taxPreview?.tax_note || form.tax_note || ''}
            </Typography>
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          <Button variant="Primary" disabled={busy} onClick={save}>
            {t('customers:billingProfile.save')}
          </Button>
          <Button variant="Secondary" disabled={busy} onClick={previewTax}>
            {t('customers:billingProfile.taxRulePreview')}
          </Button>
          <Button variant="Secondary" disabled={busy || form.vies_enabled === false} onClick={verifyVat}>
            {t('customers:billingProfile.verifyVatId')}
          </Button>
        </Stack>
        {form.vies_enabled === false && (
          <Typography sx={{ mt: 1, fontSize: 12, color: semantic.text.muted }} display="block">
            {t('customers:billingProfile.viesDisabled')}
          </Typography>
        )}
      </SectionCard>

      <Typography sx={sectionTitleSx}>{t('billing:latest_invoices')}</Typography>
      {invoicesError && <Alert severity="warning" sx={{ mb: 2 }}>{invoicesError}</Alert>}
      {openInvoices.length === 0 && invoices.length === 0 && !invoicesError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {stripeUi.showPayOnlineCta
            ? t('stripe:messages.noOpenPayment')
            : t('billing:no_invoices_yet')}
        </Alert>
      )}
      <Stack spacing={1.5}>
        {(invoices.slice(0, 8)).map((invoice) => (
          <SectionCard key={invoice.id} variant="Default" sx={{ mb: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                component={RouterLink}
                to={`/billing/invoices/${invoice.id}`}
                sx={{ fontSize: 13, color: semantic.text.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {invoice.public_number} · {invoice.status} · {invoice.total_amount} {invoice.currency}
              </Typography>
              <Typography sx={{ fontSize: 12, color: semantic.text.muted }}>
                {t('billing:invoices.pdf.status')}: {invoice.pdf_status || '—'}
              </Typography>
              {stripeUi.showPayOnlineCta && !['paid', 'void', 'canceled'].includes(invoice.status) && (
                <Button variant="Secondary" disabled={busy} onClick={() => openCheckout(invoice.id)}>
                  {t('stripe:actions.payWithStripe')}
                </Button>
              )}
              {(invoice.pdf_status !== 'generated') && (
                <Button
                  variant="Secondary"
                  disabled={busy}
                  onClick={() => handleGeneratePdf(invoice.id)}
                >
                  {invoice.pdf_status === 'failed'
                    ? t('billing:invoices.pdf.retry')
                    : t('billing:invoices.pdf.generate')}
                </Button>
              )}
              {(invoice.pdf_download_available || invoice.pdf_status === 'generated') && (
                <Button variant="Primary" disabled={busy} onClick={() => handleDownloadPdf(invoice)}>
                  {t('billing:invoices.pdf.download')}
                </Button>
              )}
              {invoice.pdf_status === 'failed' && invoice.pdf_error_message && (
                <Typography sx={{ fontSize: 12, color: semantic.warning }}>
                  {t('billing:invoices.pdf.safeError')}: {invoice.pdf_error_message}
                </Typography>
              )}
            </Stack>
          </SectionCard>
        ))}
      </Stack>
      {invoices.length === 0 && !invoicesError && (
        <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
          {t('billing:no_invoices_yet')}
        </Typography>
      )}
    </Box>
  );
}
